import {
  createContext,
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useActiveOrg } from '../providers/useActiveOrg';
import { powersyncLogger } from './logger';
import type { OrgSwitchPhase } from './orgSwitch';
import { drainUploadQueue, ORG_SWITCH_PHASE, resyncForOrganization, switchOrganization } from './orgSwitch';
import type { SyncIdentityStore } from './syncIdentity';
import { hasSyncIdentity } from './syncIdentity';
import { useSystem } from './system';

type OrgSwitchContextValue = {
  phase: OrgSwitchPhase;
  /**
   * Switches to `orgId` and replaces the local data with its own. Rejects with `OrgSwitchError`,
   * having changed nothing, when unsent changes cannot be uploaded first — tell the user why.
   */
  switchTo: (orgId: string) => Promise<void>;
  /**
   * Uploads pending changes, rejecting with `OrgSwitchError` when it cannot. For a flow that makes
   * another organization active on its own — Clerk's create-organization modal — to run first, so
   * the resync that follows the change has nothing left to misfile.
   */
  uploadPendingChanges: () => Promise<void>;
};

const OrgSwitchContext = createContext<OrgSwitchContextValue | null>(null);

type Props = {
  /** Consuming app's version, reported to PowerSync as connection metadata. */
  appVersion: string;
  /** The signed-in Clerk user, or `null` before Clerk resolves one. */
  userId: string | null;
  store: SyncIdentityStore;
  /** Makes `orgId` Clerk's active organization — `setActive({ organization })`. */
  activate: (orgId: string) => Promise<void>;
  /**
   * The organization to make active when the session names none — `pickOrganizationToActivate` over
   * the user's memberships, which only the app's own Clerk SDK can list. `null` while they load.
   */
  fallbackOrganizationId: string | null;
  /** Drawn over the app while a switch is under way; the app underneath is empty for most of it. */
  renderBlocking: (phase: OrgSwitchPhase) => ReactNode;
  /** After a switch settles: drop server caches scoped to the old organization, leave its routes. */
  onSwitched?: () => void;
};

/**
 * Owns switching organization inside a live session. Must sit inside `PowersyncSystemProvider`.
 *
 * Switching goes through `switchTo`, which uploads pending changes first — see `drainUploadQueue`.
 * A switch made some other way (another tab, a Clerk component) is still caught: the organization
 * changing under a running session triggers the same wipe and resync, minus the upload, which by
 * then would be sent with the new organization's token and filed under it.
 *
 * A session that names no organization at all gets the user's first one made active — see
 * `pickOrganizationToActivate` for why that is needed.
 */
export const OrgSwitchProvider: FC<PropsWithChildren<Props>> = ({
  children,
  appVersion,
  userId,
  store,
  activate,
  fallbackOrganizationId,
  renderBlocking,
  onSwitched,
}) => {
  const system = useSystem();
  const { clerkOrgId, source, isLoaded } = useActiveOrg();
  const [phase, setPhase] = useState<OrgSwitchPhase>(ORG_SWITCH_PHASE.idle);

  // Refs, because the safety net below must see a switch the moment it starts, not a render later.
  const switchingRef = useRef(false);
  const lastSessionOrgRef = useRef<string | null>(null);
  // One attempt per organization per mount: a failure (offline with changes still to upload) is not
  // retried in a loop, and the next start tries again.
  const autoActivatedRef = useRef<string | null>(null);
  const onSwitchedRef = useRef(onSwitched);

  useEffect(() => {
    onSwitchedRef.current = onSwitched;
  }, [onSwitched]);

  const settle = useCallback(() => {
    switchingRef.current = false;
    setPhase(ORG_SWITCH_PHASE.idle);
  }, []);

  const switchTo = useCallback(
    async (orgId: string) => {
      if (!userId || switchingRef.current || orgId === lastSessionOrgRef.current) {
        return;
      }

      switchingRef.current = true;

      try {
        await switchOrganization({
          database: system.powersync,
          connector: system.clerkConnector,
          appVersion,
          store,
          userId,
          orgId,
          activate,
          onPhase: setPhase,
        });
        onSwitchedRef.current?.();
      } finally {
        settle();
      }
    },
    [system, appVersion, store, userId, activate, settle],
  );

  const uploadPendingChanges = useCallback(() => drainUploadQueue({ database: system.powersync }), [system]);

  useEffect(() => {
    // Only a live session's organization counts. A value restored from disk on a cold offline boot
    // is not a switch, and the boot-time identity check already covers it.
    if (source !== 'session' || !clerkOrgId) {
      return;
    }

    const previous = lastSessionOrgRef.current;
    lastSessionOrgRef.current = clerkOrgId;

    if (!previous || previous === clerkOrgId || switchingRef.current || !userId) {
      return;
    }

    // Another tab sharing this database may already have switched it.
    if (hasSyncIdentity({ store, userId, orgId: clerkOrgId })) {
      return;
    }

    powersyncLogger.warn('organization changed outside switchTo, resyncing without uploading first');
    switchingRef.current = true;

    resyncForOrganization({
      database: system.powersync,
      connector: system.clerkConnector,
      appVersion,
      store,
      userId,
      orgId: clerkOrgId,
      onPhase: setPhase,
    })
      .then(() => onSwitchedRef.current?.())
      .catch((err) => {
        powersyncLogger.exception('resync after an organization change failed', err);
      })
      .finally(settle);
  }, [clerkOrgId, source, userId, system, appVersion, store, settle]);

  useEffect(() => {
    // Only once Clerk has answered, and only when its answer is "no organization": a value restored
    // from disk on a cold offline boot names one, and so does every session that already chose.
    if (!isLoaded || clerkOrgId || !userId || !fallbackOrganizationId) {
      return;
    }

    if (switchingRef.current || autoActivatedRef.current === fallbackOrganizationId) {
      return;
    }

    autoActivatedRef.current = fallbackOrganizationId;
    switchingRef.current = true;
    powersyncLogger.info('session named no organization, activating the first one');

    const activateFallbackOrganization = (orgId: string): Promise<void> => {
      if (hasSyncIdentity({ store, userId, orgId })) {
        // The rows on the device are already this organization's, so naming it is all that is missing.
        return activate(orgId);
      }

      // The rows were synced for the user alone, or for nobody yet. The next boot would wipe them
      // anyway, against the identity this records; doing it now keeps the rows and the record in step
      // for the rest of this session.
      return switchOrganization({
        database: system.powersync,
        connector: system.clerkConnector,
        appVersion,
        store,
        userId,
        orgId,
        activate,
        onPhase: setPhase,
      });
    };

    // `onSwitched` is left out on purpose: it sends the user home, and this runs on the first load,
    // often of a deep link. There was no previous organization to clear out either — with none named,
    // the backend answered as the user's own organization, which for the user with a single
    // membership (nearly everyone) is this one. A user with several may hold a few cached responses
    // from their own organization until those refetch.
    activateFallbackOrganization(fallbackOrganizationId)
      .catch((err) => {
        powersyncLogger.exception('activating the first organization failed', err);
      })
      .finally(settle);
  }, [isLoaded, clerkOrgId, userId, fallbackOrganizationId, system, appVersion, store, activate, settle]);

  const value = useMemo(() => ({ phase, switchTo, uploadPendingChanges }), [phase, switchTo, uploadPendingChanges]);

  return (
    <OrgSwitchContext.Provider value={value}>
      {children}
      {phase !== ORG_SWITCH_PHASE.idle && renderBlocking(phase)}
    </OrgSwitchContext.Provider>
  );
};

export const useOrgSwitch = (): OrgSwitchContextValue => {
  const context = useContext(OrgSwitchContext);
  if (!context) {
    throw new Error('useOrgSwitch must be used within an OrgSwitchProvider');
  }
  return context;
};
